'use client';

import { MAX_BIO_LENGTH, updateBioInput } from '@api/routers/user/validation';
import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';

import { useCurrentCompletedUserOrThrow } from '@web/features/user/service';
import { Button, showSnackbar, TextField } from '@web/ui/components';
import { defaultFormHandler } from '@web/utils/form/defaultFormHandler';
import { trpcReact } from '@web/utils/trpc/trpcReact';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing('1000')};
`;

const BioTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme }) => theme.color.onSurface};
  padding-bottom: ${({ theme }) => theme.spacing('1000')};
`;

const BioLabel = styled.span`
  font: ${({ theme }) => theme.typography.body.regular};
`;

const BioCounter = styled.span<{ limitReached: boolean }>`
  font: ${({ theme }) => theme.typography.body.bold};
  color: ${({ theme, limitReached }) => limitReached && theme.color.onRedFixed};
`;

const BioDescription = styled.p`
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  opacity: ${({ theme }) => theme.opacity.dense};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
  font: ${({ theme }) => theme.typography.body.regular};
  text-transform: ${({ theme }) => theme.textTransform.capitalize};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  padding-top: ${({ theme }) => theme.spacing('1000')};
  padding-bottom: ${({ theme }) => theme.spacing('2000')};
`;

type FormValues = {
  bio: string;
};

export const ProfileForm = () => {
  const t = useTranslations();
  const { user } = useCurrentCompletedUserOrThrow();

  const updateBio = trpcReact.user.updateBio.useMutation({
    onSuccess: () => showSnackbar(t('SETTINGS_PAGE_PROFILE_SAVE_SUCCESS')),
    onError: () =>
      showSnackbar(t('SETTINGS_PAGE_PROFILE_SAVE_ERROR'), {
        severity: 'error',
      }),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting, errors },
    setError,
  } = useForm<FormValues>({
    defaultValues: { bio: user.bio },
    resolver: zodResolver(updateBioInput),
  });

  const watchBio = watch('bio');
  const charactersLeft = MAX_BIO_LENGTH - watchBio.length;

  return (
    <FormContainer
      onSubmit={handleSubmit(
        defaultFormHandler(updateBio.mutateAsync, setError),
      )}
    >
      <BioTopRow>
        <BioLabel>{t('SETTINGS_PAGE_PROFILE_BIO_LABEL')}</BioLabel>
        <BioCounter limitReached={charactersLeft === 0}>
          {charactersLeft}
        </BioCounter>
      </BioTopRow>
      <TextField
        {...register('bio')}
        placeholder={t('SETTINGS_PAGE_PROFILE_BIO_PLACEHOLDER')}
        rows={5}
        inputProps={{ maxLength: MAX_BIO_LENGTH }}
        multiline
        error={errors.bio?.message}
      />
      <BioDescription>
        {t('SETTINGS_PAGE_PROFILE_BIO_DESCRIPTION')}
      </BioDescription>
      <Button
        type="submit"
        label={
          isSubmitting
            ? t('SETTINGS_PAGE_PROFILE_SAVING')
            : t('SETTINGS_PAGE_PROFILE_SAVE')
        }
        disabled={isSubmitting || !isDirty}
        fullWidth
      />
    </FormContainer>
  );
};
